import { Button, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import React, { useState } from 'react';
import { InventorySupplierFormState } from './supplierForm';

interface SupplierEditorModalProps {
  initial: InventorySupplierFormState;
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: InventorySupplierFormState) => void;
}

export function SupplierEditorModal({
  initial,
  isSaving,
  title,
  onClose,
  onSubmit,
}: SupplierEditorModalProps) {
  const [form, setForm] = useState<InventorySupplierFormState>(initial);

  const set = <K extends keyof InventorySupplierFormState>(
    key: K,
    value: InventorySupplierFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Supplier name"
            required
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="e.g. Fresh Farm Supplies"
          />
          <FormInput
            label="Contact person"
            value={form.contactPerson}
            onChange={(event) => set('contactPerson', event.target.value)}
            placeholder="e.g. Grace Namara"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Phone"
            value={form.phone}
            onChange={(event) => set('phone', event.target.value)}
            placeholder="e.g. +256700000000"
          />
          <FormInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
            placeholder="e.g. supplier@example.com"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Tax ID"
            value={form.taxId}
            onChange={(event) => set('taxId', event.target.value)}
            placeholder="Optional tax registration number"
          />
          <FormInput
            label="Payment terms (days)"
            required
            type="number"
            min="0"
            step="1"
            value={form.paymentTermsDays}
            onChange={(event) => set('paymentTermsDays', event.target.value)}
            placeholder="e.g. 30"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-[1.2fr_0.8fr]">
          <FormTextarea
            label="Address"
            value={form.address}
            onChange={(event) => set('address', event.target.value)}
            placeholder="Optional supplier address"
            rows={3}
          />
          <FormInput
            label="Opening balance"
            required
            type="number"
            min="0"
            step="0.01"
            value={form.openingBalance}
            onChange={(event) => set('openingBalance', event.target.value)}
            placeholder="Starting payable balance"
          />
        </div>

        <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-700">
          <input
            type="checkbox"
            aria-label="Supplier is active"
            checked={form.isActive}
            onChange={(event) => set('isActive', event.target.checked)}
          />
          Supplier is active
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save supplier
          </Button>
        </div>
      </form>
    </Modal>
  );
}
